import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, PieChart, Pie, Cell, Legend,
} from "recharts";
import { Clock, GitCompareArrows, TrendingUp, Inbox } from "lucide-react";

const AXIS_STYLE = { fontSize: 11, fill: "#64748b" };
const TOOLTIP_STYLE = {
  backgroundColor: "white",
  border: "1px solid #e2e8f0",
  borderRadius: 6, color: "#0f172a", fontSize: 12,
};
const STATUS_COLORS = {
  draft: "#94a3b8",
  rfq_generated: "#38bdf8",
  sent: "#38bdf8",
  quote_received: "#10b981",
  closed: "#d4a24c",
  order_pending: "#f59e0b",
};

function pretty(k) { return k.replace(/_/g, " "); }

function Panel({ title, subtitle, children, testId }) {
  return (
    <div className="surface p-4 md:p-5" data-testid={testId}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="section-title">{title}</div>
          {subtitle && <div className="text-xs text-slate-500 mt-0.5">{subtitle}</div>}
        </div>
      </div>
      {children}
    </div>
  );
}

function EmptyChart({ msg }) {
  return (
    <div data-testid="empty-chart" className="h-48 flex items-center justify-center text-xs text-slate-400 border border-dashed border-slate-200 rounded-md">
      {msg}
    </div>
  );
}

function fmtHours(h) {
  if (h == null) return "—";
  if (h < 1) return `${Math.round(h * 60)}m`;
  if (h < 48) return `${h.toFixed(1)}h`;
  return `${(h / 24).toFixed(1)}d`;
}

export function BuyerAnalyticsPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/analytics/buyer")
      .then(({ data }) => setData(data))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="grid grid-cols-2 md:grid-cols-4 gap-3"><div className="skeleton h-24"/><div className="skeleton h-24"/><div className="skeleton h-24"/><div className="skeleton h-24"/></div>;
  if (!data) return null;

  const s = data.stats;
  const anyActivity = data.activity_series.some((x) => x.count > 0);
  const firstCmp = data.comparisons?.[0];

  const metrics = [
    { label: "Active RFQs", value: s.active_rfqs, Icon: Inbox },
    { label: "RFQs sent", value: s.rfqs_sent, Icon: GitCompareArrows },
    { label: "Supplier responses", value: s.supplier_responses, Icon: TrendingUp },
    { label: "Quotes received", value: s.quotes_received, Icon: Inbox },
    { label: "Avg response time", value: fmtHours(s.avg_response_hours), Icon: Clock, hint: s.avg_response_hours == null ? "Needs more data" : null },
    { label: "Response rate", value: s.response_rate == null ? "—" : `${Math.round(s.response_rate * 100)}%`, Icon: TrendingUp, hint: s.response_rate == null ? "Needs sent RFQs" : null },
  ];

  return (
    <section className="space-y-5" data-testid="buyer-analytics-panel">
      {/* Metric strip */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {metrics.map((m) => (
          <div key={m.label} className="surface p-4" data-testid={`metric-${m.label.toLowerCase().replace(/\s+/g, '-')}`}>
            <div className="flex items-center justify-between">
              <div className="text-[11px] uppercase tracking-wider text-slate-500">{m.label}</div>
              <m.Icon size={13} className="text-slate-400"/>
            </div>
            <div className="text-xl font-semibold tracking-tight mt-1 tabular-nums">{m.value}</div>
            {m.hint && <div className="text-[10px] text-slate-400 mt-1">{m.hint}</div>}
          </div>
        ))}
      </div>

      {/* Charts */}
      <div className="grid md:grid-cols-2 gap-4">
        <Panel title="RFQ activity" subtitle="RFQs created, last 30 days" testId="chart-activity">
          {anyActivity ? (
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data.activity_series} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                  <CartesianGrid stroke="#e2e8f0" vertical={false}/>
                  <XAxis dataKey="date" tick={AXIS_STYLE} interval={5} tickFormatter={(d)=>d.slice(5)}/>
                  <YAxis allowDecimals={false} tick={AXIS_STYLE}/>
                  <Tooltip contentStyle={TOOLTIP_STYLE}/>
                  <Line type="monotone" dataKey="count" stroke="#d4a24c" strokeWidth={2} dot={{ r: 2, fill: "#d4a24c" }} activeDot={{ r: 4 }}/>
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : <EmptyChart msg="No RFQs created in the last 30 days."/>}
        </Panel>

        <Panel title="RFQ status distribution" subtitle="Breakdown of your current RFQs" testId="chart-status">
          {data.status_series.length > 0 ? (
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={data.status_series.map(x => ({ name: pretty(x.status), raw: x.status, value: x.count }))}
                       dataKey="value" nameKey="name" innerRadius={44} outerRadius={76} paddingAngle={2}>
                    {data.status_series.map((x, i) => <Cell key={i} fill={STATUS_COLORS[x.status] || "#94a3b8"}/>)}
                  </Pie>
                  <Tooltip contentStyle={TOOLTIP_STYLE}/>
                  <Legend wrapperStyle={{ fontSize: 11, color: "#64748b" }}/>
                </PieChart>
              </ResponsiveContainer>
            </div>
          ) : <EmptyChart msg="No RFQs yet."/>}
        </Panel>

        <Panel title="Quotation price comparison" subtitle={firstCmp ? firstCmp.title : "Latest multi-quote RFQ"} testId="chart-price-compare">
          {firstCmp ? (
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={firstCmp.quotes.map(q => ({ name: q.supplier_name?.split(" ").slice(0,2).join(" ") || "—", price: q.price, is_demo: q.is_demo }))} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                  <CartesianGrid stroke="#e2e8f0" vertical={false}/>
                  <XAxis dataKey="name" tick={AXIS_STYLE} interval={0} angle={-15} textAnchor="end" height={56}/>
                  <YAxis tick={AXIS_STYLE}/>
                  <Tooltip contentStyle={TOOLTIP_STYLE} formatter={(v)=>`${firstCmp.currency} ${v?.toLocaleString()}`}/>
                  <Bar dataKey="price" radius={[4,4,0,0]}>
                    {firstCmp.quotes.map((q, i) => <Cell key={i} fill={q.is_demo ? "#38bdf8" : "#d4a24c"}/>)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : <EmptyChart msg="Need at least 2 quotations on one RFQ."/>}
        </Panel>

        <Panel title="Lead-time comparison" subtitle={firstCmp ? firstCmp.title : "Latest multi-quote RFQ"} testId="chart-lead-compare">
          {firstCmp ? (
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart layout="vertical" data={firstCmp.quotes.map(q => ({ name: q.supplier_name?.split(" ").slice(0,2).join(" ") || "—", lead: q.lead_time_days || 0 }))} margin={{ top: 5, right: 20, left: 0, bottom: 0 }}>
                  <CartesianGrid stroke="#e2e8f0" horizontal={false}/>
                  <XAxis type="number" tick={AXIS_STYLE}/>
                  <YAxis type="category" dataKey="name" tick={AXIS_STYLE} width={110}/>
                  <Tooltip contentStyle={TOOLTIP_STYLE} formatter={(v)=>`${v} days`}/>
                  <Bar dataKey="lead" fill="#0f172a" radius={[0,4,4,0]}/>
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : <EmptyChart msg="Need at least 2 quotations on one RFQ."/>}
        </Panel>
      </div>
      <div className="text-[11px] text-slate-500">Analytics are computed from your stored RFQs and quotations only. We never fabricate numbers.</div>
    </section>
  );
}
